import React, {useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faYoutube,
  faTwitter,
  faLinkedinIn,
  faTiktok,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import {faChevronDown} from '@fortawesome/free-solid-svg-icons';

import Link from './Link';
import Brand, {BrandMark, getBrandName} from './Brand';
import EmailSignUp from './EmailSignUp';
import {useAppContext} from '../hooks';
import {shouldHideCoaching, shouldHideJobReferrals} from '../util';

const sectionLink = 'text-[#5f6368] block my-1 transition-colors hover:text-[#1c1b24]';
const headingClasses = 'text-sm font-semibold text-gray-900 tracking-tight';

/* Columns longer than this collapse behind a "See more" toggle. */
const TRUNCATE_AFTER = 10;

const FooterSection = ({title, children}) => {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const media = window.matchMedia('(min-width: 768px)');
    const sync = () => {
      if (media.matches) el.setAttribute('open', '');
      else el.removeAttribute('open');
    };
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  const [expanded, setExpanded] = useState(false);
  const items = React.Children.toArray(children);
  const visible = items.slice(0, TRUNCATE_AFTER);
  const overflow = items.slice(TRUNCATE_AFTER);

  return (
    <details ref={ref} open className="group text-center md:text-left border-b border-gray-200 md:border-b-0 py-2 md:py-0">
      <summary
        className={`${headingClasses} flex items-center justify-between md:justify-start gap-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden md:cursor-default md:pointer-events-none md:mb-3`}
      >
        <span>{title}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          aria-hidden="true"
          className="w-3 h-3 transition-transform md:hidden group-open:rotate-180"
        />
      </summary>
      <div className="pt-2 pb-3 md:pt-0 md:pb-0">
        {visible}
        {overflow.length > 0 && (
          <>
            {/* Kept in the markup for crawlers; only visually hidden. */}
            <div className={expanded ? undefined : 'hidden'}>{overflow}</div>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="mt-1 text-sm text-gray-400 transition-colors hover:text-gray-600"
            >
              {expanded ? 'See less' : 'See more...'}
            </button>
          </>
        )}
      </div>
    </details>
  );
};

const Footer = () => {
  const {currentUser} = useAppContext();
  const hideCoaching = shouldHideCoaching(currentUser);
  const hideReferrals = shouldHideJobReferrals(currentUser);

  const socialLinks = [
    {href: 'https://www.youtube.com/@aced_io?sub_confirmation=1', label: 'Subscribe to Aced on YouTube', icon: faYoutube},
    {href: 'https://www.linkedin.com/company/aced-io', label: 'Follow Aced on LinkedIn', icon: faLinkedinIn},
    {href: 'https://www.tiktok.com/@aced_io', label: 'Follow Aced on TikTok', icon: faTiktok},
    {href: 'https://www.instagram.com/aced__io/', label: 'Follow Aced on Instagram', icon: faInstagram},
    {href: 'https://www.facebook.com/tryexponent', label: 'Like Aced on Facebook', icon: faFacebookF},
    {href: 'https://x.com/aced_io', label: 'Follow Aced on X', icon: faTwitter},
  ];

  return (
    <div className="bg-[#f9f9f9] pt-10 pb-6 text-[#5f6368] text-base">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 pb-8 border-b border-gray-200 mb-8">
          <div className="text-center md:text-left max-w-sm mx-auto md:mx-0">
            <Brand className="mb-3 justify-center md:justify-start" markClassName="h-7 w-7" wordmarkClassName="ml-2.5" fkaBadge />
            <p className="mx-auto md:mx-0">Get updates in your inbox with the latest tips, job listings, and more.</p>
            {!currentUser && (
              <EmailSignUp
                parent="Footer__newsletter"
                src="leads_footer"
                formClassName="mt-4 flex items-stretch justify-center gap-2 md:justify-start"
                inputClassName="w-full max-w-60 rounded-lg border border-gray-300 bg-white px-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none"
              />
            )}
          </div>
          <div className="text-center md:text-right">
            <h2 className={`${headingClasses} block mb-3.5`}>Follow Us</h2>
            <div className="flex justify-center md:justify-end flex-wrap gap-3">
              {socialLinks.map((social) => (
                <Link
                  key={social.href}
                  title={social.label}
                  aria-label={social.label}
                  href={social.href}
                >
                  <FontAwesomeIcon icon={social.icon} aria-hidden="true" className="text-black w-4 h-4 opacity-80" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-0 md:gap-y-8">
          <FooterSection title="Products">
            <Link href="/upgrade?src=footer" className={sectionLink}>Pricing</Link>
            <Link href="/courses?src=footer" className={sectionLink}>Courses</Link>
            {!hideCoaching && <Link href="/coaching?src=footer" className={sectionLink}>Coaching</Link>}
            <Link href="/guides?src=footer" className={sectionLink}>Interview Guides</Link>
            <Link href="/experiences?src=footer" className={sectionLink}>Interview Experiences</Link>
            <Link href="/questions?src=footer" className={sectionLink}>Interview Questions</Link>
            <Link href="/practice?src=footer" className={sectionLink}>Peer Mock Interviews</Link>
            <Link href="/jobs?src=footer" className={sectionLink}>Job Boards</Link>
            {!hideReferrals && <Link href="/jobs/referrals?src=footer" className={sectionLink}>Job Referrals</Link>}
            <Link href="/companies?src=footer" className={sectionLink}>All Companies</Link>
          </FooterSection>
          <FooterSection title="Courses">
            <Link href="/courses/pm?src=footer" className={sectionLink}>Product Management</Link>
            <Link href="/courses/software-engineering?src=footer" className={sectionLink}>Software Engineering</Link>
            <Link href="/courses/forward-deployed-engineering?src=footer" className={sectionLink}>Forward Deployed Engineering</Link>
            <Link href="/courses/ai-engineering?src=footer" className={sectionLink}>AI Engineering</Link>
            <Link href="/courses/system-design-interviews?src=footer" className={sectionLink}>System Design</Link>
            <Link href="/ib?src=footer" className={sectionLink}>Investment Banking</Link>
            <Link href="/courses/product-analytics?src=footer" className={sectionLink}>Product Analytics</Link>
            <Link href="/courses/data-engineering?src=footer" className={sectionLink}>Data Engineer</Link>
            <Link href="/courses/security-engineer?src=footer" className={sectionLink}>Security Engineering</Link>
            <Link href="/courses/security-analyst?src=footer" className={sectionLink}>Security Analyst</Link>
            <Link href="/courses/engineering-management?src=footer" className={sectionLink}>Engineering Management</Link>
            <Link href="/courses/tpm?src=footer" className={sectionLink}>Technical Program Management</Link>
            <Link href="/courses/product-designer-interview?src=footer" className={sectionLink}>UX / Product Design</Link>
            <Link href="/courses/data-science?src=footer" className={sectionLink}>Data Science</Link>
            <Link href="/courses/sql-interviews?src=footer" className={sectionLink}>SQL Interviews</Link>
            <Link href="/courses/solution-architect-interview?src=footer" className={sectionLink}>Solutions Architect</Link>
            <Link href="/courses/bizops?src=footer" className={sectionLink}>BizOps & Strategy</Link>
            <Link href="/courses/ml-engineer?src=footer" className={sectionLink}>Machine Learning</Link>
            <Link href="/courses/eng-behavioral?src=footer" className={sectionLink}>Engineering Behavioral</Link>
            <Link href="/courses/gen-ai-interviews?src=footer" className={sectionLink}>Generative AI</Link>
          </FooterSection>
          <FooterSection title="Interview Questions">
            <Link href="/questions?role=pm&src=footer" className={sectionLink}>PM Interview Questions</Link>
            <Link href="/questions?role=tpm&src=footer" className={sectionLink}>TPM Interview Questions</Link>
            <Link href="/questions?role=em&src=footer" className={sectionLink}>EM Interview Questions</Link>
            <Link href="/questions?role=swe&src=footer" className={sectionLink}>Software Engineering Interview Questions</Link>
            <Link href="/questions?role=forward-deployed-engineer&src=footer" className={sectionLink}>Forward Deployed Engineering Interview Questions</Link>
            <Link href="/questions?role=investment-banking&src=footer" className={sectionLink}>Investment Banking Interview Questions</Link>
            <Link href="/questions?role=data-engineer&src=footer" className={sectionLink}>Data Engineering Interview Questions</Link>
            <Link href="/questions?role=solutions-architect&src=footer" className={sectionLink}>Solutions Architect Interview Questions</Link>
            <Link href="/questions?role=bizops&src=footer" className={sectionLink}>BizOps Interview Questions</Link>
            <Link href="/questions?role=product-designer&src=footer" className={sectionLink}>Designer Interview Questions</Link>
            <Link href="/questions?role=ml-engineer&src=footer" className={sectionLink}>Machine Learning Interview Questions</Link>
            <Link href="/questions?role=data-science&src=footer" className={sectionLink}>Data Science Interview Questions</Link>
          </FooterSection>
          <FooterSection title="Interview Experiences">
            <Link href="/experiences?src=footer" className={sectionLink}>All Experiences</Link>
            <Link href="/experiences?verified=true&src=footer" className={sectionLink}>Verified Experiences</Link>
            <Link href="/questions/contribute?src=footer" className={sectionLink}>Share Interview Experience</Link>
            <Link href="/experiences?role=pm&src=footer" className={sectionLink}>PM Experiences</Link>
            <Link href="/experiences?role=em&src=footer" className={sectionLink}>EM Experiences</Link>
            <Link href="/experiences?role=swe&src=footer" className={sectionLink}>SWE Experiences</Link>
            <Link href="/experiences?company=openai&src=footer" className={sectionLink}>OpenAI Experiences</Link>
            <Link href="/experiences?company=anthropic&src=footer" className={sectionLink}>Anthropic Experiences</Link>
            <Link href="/experiences?company=google&src=footer" className={sectionLink}>Google Experiences</Link>
            <Link href="/experiences?company=meta-facebook&src=footer" className={sectionLink}>Meta Experiences</Link>
          </FooterSection>
          <FooterSection title="Popular articles">
            <Link href="/blog/system-design-interview-guide?src=footer" className={sectionLink}>Ace the System Design Interview</Link>
            <Link href="/blog/top-product-manager-interview-questions/?src=footer" className={sectionLink}>Top Product Manager Interview Questions</Link>
            <Link href="/blog/how-to-nail-amazons-behavioral-interview-questions/?src=footer" className={sectionLink}>Amazon Behavioral Interview Questions</Link>
            <Link href="/blog/best-tech-companies-to-work-for/?src=footer" className={sectionLink}>Best Tech Companies To Work For</Link>
            <Link href="/blog/how-to-prepare-for-an-engineering-manager-interview?src=footer" className={sectionLink}>Ace the Engineering Manager Interview</Link>
            <Link href="/blog?src=footer" className={sectionLink}>{`${getBrandName()} Blog`}</Link>
          </FooterSection>
          <FooterSection title="Guides">
            <Link href="/guides/google-product-manager-interview?src=footer" className={sectionLink}>Google PM Interviews</Link>
            <Link href="/guides/amazon-solutions-architect-interview?src=footer" className={sectionLink}>AWS Solutions Architect Interviews</Link>
            <Link href="/guides/amazon-product-manager-interview?src=footer" className={sectionLink}>Amazon PM Interviews</Link>
            <Link href="/guides/google-engineering-manager-interview?src=footer" className={sectionLink}>Google EM Interviews</Link>
            <Link href="/guides/amazon-software-development-manager-interview?src=footer" className={sectionLink}>Amazon SDM Interviews</Link>
            <Link href="/guides/microsoft-product-manager-interview?src=footer" className={sectionLink}>Microsoft PM Interviews</Link>
          </FooterSection>
          {!hideCoaching && (
            <FooterSection title="Coaching">
              <Link href="/coaching?src=footer" className={sectionLink}>1:1 Coaching</Link>
              <Link href="/coaching?category=mock_interviews&src=footer" className={sectionLink}>Mock Interviews</Link>
              <Link href="/resume?src=footer" className={sectionLink}>Resume Review</Link>
              <Link href="/coaching?category=career_coaching&src=footer" className={sectionLink}>Career Coaching</Link>
              <Link href="/coaching?category=negotiation&src=footer" className={sectionLink}>Negotiation</Link>
            </FooterSection>
          )}
          <FooterSection title="For Partners">
            <Link href="/affiliate?src=footer" className={sectionLink}>Affiliate Program</Link>
            <Link href="/collaborate" className={sectionLink}>Work With Us</Link>
            <Link href="/pages/university" className={sectionLink}>For Universities</Link>
            <Link href="/pages/business" className={sectionLink}>For Businesses</Link>
          </FooterSection>
          <FooterSection title="Company">
            <Link href="/collaborate" className={sectionLink}>Work With Us</Link>
            <Link href="/about" className={sectionLink}>{`About ${getBrandName()}`}</Link>
            <Link href="/support?src=footer" className={sectionLink}>Support and FAQ</Link>
            <Link href="/reviews?src=footer" className={sectionLink}>Reviews</Link>
            <Link href="/blog?src=footer" className={sectionLink}>{`${getBrandName()} Blog`}</Link>
          </FooterSection>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-center md:text-left mt-8 pt-6 border-t border-gray-200 text-sm">
          <div>
            Exponent Labs, LLC ©
            {' '}
            {(new Date()).getFullYear()}
          </div>
          <div>
            <Link href="/tos" className="text-[#5f6368]">Terms of Service</Link>
            {' | '}
            <Link href="/privacy" className="text-[#5f6368]">Privacy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The stripped-back footer used inside the logged-in shell and on focused
 * flows. Members navigate from the sidebar, so the full sitemap footer is
 * pure noise below every page; this keeps only the legal and support links
 * that have to remain reachable.
 */
export const MinimalFooter = () => (
  <div className="container mx-auto px-4">
    <div className="flex flex-wrap items-center justify-between gap-2 text-[#5f6368] text-sm py-4">
      <div className="flex items-center gap-2">
        <BrandMark className="h-4 w-4" />
        <span>
          {'Exponent Labs, LLC \u00a9 '}
          {(new Date()).getFullYear()}
        </span>
      </div>
      <div>
        <Link href="/tos" className="text-[#5f6368]">Terms of Use</Link>
        {' \u2022 '}
        <Link href="/privacy" className="text-[#5f6368]">Privacy</Link>
        {' \u2022 '}
        <Link href="/support" className="text-[#5f6368]">Support</Link>
      </div>
    </div>
  </div>
);

export default Footer;
